import React, { useContext, useEffect, useState } from 'react';
import './categoryConfig.css';
import {
    USBGrid,
    USBColumn,
    USBButton,
    USBDropdown,
    USBInput,
} from '@usb-shield/components-react';
import USBModal, { ModalHeader, ModalBody, ModalFooter } from '@usb-shield/react-modal';
import USBBreadcrumb from '@usb-shield/components-react/dist/cjs/breadcrumb';
import { FeatureCategoryContext } from '../../components/Hooks/useFeatureCategories';
import callApi from '../../components/utilities/callApi';
import { endpoints } from '../../../config/connections';
import Messages from '../../components/organisms/Messages/Messages';
import { USBIconDelete } from '@usb-shield/react-icons';

function CategoryCard({ title, items, onDeleteItem, deletingItem }) {
    return (
        <div className="category-card">
            <div className="category-card__header">
                <h3 className="category-card__title">{title}</h3>
            </div>
            <ul className="category-card__list">
                {items.map((item) => (
                    <li key={item} className="category-card__item">
                        <span className="category-card__item-text">{item}</span>
                        <span className="category-card__delete-icon" onClick={() => onDeleteItem(item)}>
                            <USBIconDelete
                                aria-label={`Delete ${item}`}
                            />
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function closeMessage(setFailedFlag, setSuccessFlag) {
    setFailedFlag(false);
    setSuccessFlag(false);
}

function CategoryConfig() {
    const { categories, setCategories } = useContext(FeatureCategoryContext);
    const categoryOptions = categories.map(({ title, featureName }) => ({
        key: featureName,
        value: title,
    }));
    const defaultOption = categoryOptions[0] || { key: '', value: '' };
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedKey, setSelectedKey] = useState(defaultOption.key);
    const [inputValue, setInputValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [successFlag, setSuccessFlag] = useState(false);
    const [failedFlag, setFailedFlag] = useState(false);
    const [messageContent, setMessageContent] = useState('');
    const [errorContent, setErrorContent] = useState('');
    const [deletingItemKey, setDeletingItemKey] = useState(''); // `${featureName}::${item}`
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null); // { category, item }

    async function addFeatureCategory(payload) {
        const response = await callApi.postApi(`${endpoints.apigee}/document-management/coreservice/user-management/v1/featureCategory/update`, payload);
        return response;
    }

    async function handleAdd() {
        const trimmed = inputValue.trim();
        if (!trimmed || submitting) return;

        const targetCategory = categories.find((cat) => cat.featureName === selectedKey);
        if (!targetCategory) return;

        const duplicateExists = targetCategory.items.some(
            (item) => item.trim().toLowerCase() === trimmed.toLowerCase()
        );

        if (duplicateExists) {
            setErrorContent(`"${trimmed}" already exists in ${targetCategory.title}`);
            setSuccessFlag(false);
            setFailedFlag(true);
            return;
        }

        const updatedItems = [...targetCategory.items, trimmed];
        const payload = {
            featureName: targetCategory.featureName,
            description:
                targetCategory.description || `List of categories for ${targetCategory.title}`,
            allowedCategories: updatedItems,
            active: targetCategory.active ?? true,
        };

        setSubmitting(true);

        try {
            await addFeatureCategory(payload);

            setCategories((prev) =>
                prev.map((cat) =>
                    cat.featureName === selectedKey ? { ...cat, items: updatedItems } : cat
                )
            );

            setInputValue('');
            setDialogOpen(false);

            setMessageContent(`"${trimmed}" added successfully to ${targetCategory.title}`);
            setFailedFlag(false);
            setSuccessFlag(true);
        } catch (err) {
            setErrorContent(err.message || 'Something went wrong while adding the category item.');
            setSuccessFlag(false);
            setFailedFlag(true);
        } finally {
            setSubmitting(false);
        }
    }

    function handleDeleteRequest(category, item) {
        setPendingDelete({ category, item });
        setDeleteConfirmOpen(true);
    }

    function handleDeleteCancel() {
        setPendingDelete(null);
        setDeleteConfirmOpen(false);
    }

    async function handleDeleteConfirm() {
        if (!pendingDelete) return;
        const { category, item } = pendingDelete;
        const itemKey = `${category.featureName}::${item}`;

        if (deletingItemKey) return; // prevent parallel deletes

        setDeleteConfirmOpen(false);

        const updatedItems = category.items.filter(
            (existing) => existing.toLowerCase() !== item.toLowerCase()
        );

        const payload = {
            featureName: category.featureName,
            description:
                category.description || `List of categories for ${category.title}`,
            allowedCategories: updatedItems,
            active: category.active ?? true,
        };

        setDeletingItemKey(itemKey);

        try {
            await addFeatureCategory(payload);

            setCategories((prev) =>
                prev.map((cat) =>
                    cat.featureName === category.featureName
                        ? { ...cat, items: updatedItems }
                        : cat
                )
            );

            setMessageContent(`"${item}" deleted successfully from ${category.title}`);
            setErrorContent('');
            setFailedFlag(false);
            setSuccessFlag(true);
        } catch (err) {
            setErrorContent(err.message || `Failed to delete "${item}" from ${category.title}`);
            setSuccessFlag(false);
            setFailedFlag(true);
        } finally {
            setDeletingItemKey('');
            setPendingDelete(null);
        }
    }

    function handleCancel() {
        setInputValue('');
        setSelectedKey(defaultOption.key);
        setDialogOpen(false);
    }

    return (
        <>
            <USBGrid addClasses="content-frame">
                <USBColumn largeSpan={8}>
                    <USBBreadcrumb
                        id="categoryConfigs"
                        text="Tools"
                        breadcrumbs={[
                            { id: 1, text: 'Category Configs', href: '' },
                        ]}
                    />
                </USBColumn>
                <USBColumn largeSpan={8}></USBColumn>
            </USBGrid>
            <USBGrid addClasses="content-frame content-bg-portal">
                {/* {loading ? <Loader /> : null} */}
                <USBColumn largeSpan={16}>
                    <div className="category-config">
                        <div className="category-config__toolbar">
                            <h2 className="category-config__heading">Category Configuration</h2>
                            <USBButton
                                id="addCategoryBtn"
                                variant="primary"
                                handleClick={() => setDialogOpen(true)}
                            >
                                Add Category
                            </USBButton>
                        </div>

                        {(successFlag || failedFlag) && (
                            <div
                                className="message-content"
                                data-testid="messageContent"
                                role="alert"
                            >
                                <Messages
                                    success={successFlag}
                                    singleUploadFail={failedFlag}
                                    closeMessage={closeMessage}
                                    messageContent={messageContent}
                                    errorContent={errorContent}
                                    setFailedFlag={setFailedFlag}
                                    setSuccessFlag={setSuccessFlag}
                                />
                            </div>
                        )}

                        {dialogOpen && (
                            <div className="add-category-panel" role="region" aria-label="Add Category Item">
                                <div className="add-category-panel__header">
                                    <h3 className="add-category-panel__title">Add Category Item</h3>
                                    <button
                                        type="button"
                                        className="add-category-panel__close"
                                        onClick={handleCancel}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>

                                <div className="add-category-panel__body">
                                    <div className="add-category-modal__body">
                                        <USBDropdown
                                            id="categoryDropdown"
                                            label="Category Type"
                                            items={categoryOptions}
                                            handleChange={(e, item) => {
                                                setSelectedKey(item.key);
                                            }}
                                            ariaLabel="Category type"
                                        />
                                        <USBInput
                                            id="categoryInput"
                                            labelText="New Category Item"
                                            value={inputValue}
                                            handleChange={(e) => setInputValue(e.target.value)}
                                            placeholder="Enter category item"
                                            addClasses="add-category-modal__input"
                                        />
                                    </div>
                                </div>

                                <div className="add-category-panel__footer">
                                    <div className="add-category-modal__footer">
                                        <USBButton
                                            id="addCategoryCancel"
                                            handleClick={handleCancel}
                                        >
                                            Cancel
                                        </USBButton>
                                        <USBButton
                                            id="addCategoryConfirm"
                                            variant="primary"
                                            handleClick={handleAdd}
                                        >
                                            Add
                                        </USBButton>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="category-config__grid">
                            {categories.map((category) => (
                                <CategoryCard
                                    key={category.key}
                                    title={category.title}
                                    items={category.items}
                                    deletingItem={
                                        deletingItemKey?.startsWith(`${category.featureName}::`)
                                            ? deletingItemKey.split('::')[1]
                                            : ''
                                    }
                                    onDeleteItem={(item) => handleDeleteRequest(category, item)}
                                />
                            ))}
                        </div>
                    </div>

                    <USBModal id="category-delete-modal" addClasses="delete-confirm-modal" isOpen={deleteConfirmOpen} handleClose={handleDeleteCancel}>
                        <ModalHeader id="category-delete-modal-header">Confirm Delete</ModalHeader>
                        <ModalBody>
                            <div className="deletetextframe">
                                Are you sure you want to delete{' '}
                                <strong>"{pendingDelete?.item}"</strong> from{' '}
                                <strong>{pendingDelete?.category?.title}</strong>?
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <div className="deletebtnmodalframe">
                                <span className="cancel-btn">
                                    <USBButton
                                        id="deleteCancelBtn"
                                        handleClick={handleDeleteCancel}
                                    >
                                        Cancel
                                    </USBButton>
                                </span>
                                <USBButton
                                    variant="primary"
                                    id="deleteConfirmBtn"
                                    handleClick={handleDeleteConfirm}
                                >
                                    Delete
                                </USBButton>
                            </div>
                        </ModalFooter>
                    </USBModal>
                </USBColumn>
            </USBGrid>
        </>
    );
}

export default CategoryConfig;
